// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadCurrentCountries from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import PardotForm from "@mcmc/App/Components/PardotForm/PardotForm";
import loadSystemList from "@mcmc/App/Handlers/CommonHandlers/LoadSystemList";
import McMcApplicationState from "@mcmc/App/Store/McMcApplicationState";
import { getEventData, hasEnoughSeats } from "@mcmc/App/Store/Selectors/ProductDetailsSelectors";
import { getSettingsCollection } from "@mcmc/App/Store/Selectors/SettingsSelectors";
import { getSystemList } from "@mcmc/App/Store/Selectors/SystemListSelectors";
import EventRegistrationForm from "@mcmc/App/Widgets/EventRegistration/EventRegistrationForm/EventRegistrationForm";
import {
    EventRegistrationFormState,
    initailEventRegistrationFormState,
} from "@mcmc/App/Widgets/EventRegistration/EventRegistrationForm/EventRegistrationForm.helper";
import { ReCaptchaStyles } from "@mcmc/Overrides/Components/ReCaptcha";
import { pageTitleStyles } from "@mcmc/Overrides/Widgets/ProductDetails/ProductDetailsPageTitle";
import StyledBox from "@mcmc/XDL/Components/Common/Box";
import cloneDeep from "lodash/cloneDeep";
import React, { useCallback, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

type OwnProps = HasProductContext & HasHistory & HasToasterContext & WidgetProps;

const mapStateToProps = (state: McMcApplicationState, props: OwnProps) => ({
    customSettings: getSettingsCollection(state).customSettings,
    eventData: getEventData(state, props.productContext.product),
    hasEnoughSeats: hasEnoughSeats(props.productContext),
    language: state.context.session.language,
    countries: getCurrentCountries(state),
    contactUsOptions: getSystemList(state, "ContactUsOptions"),
});

const mapDispatchToProps = {
    loadCurrentCountries,
    loadSystemList,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface EventRegistrationStyles {
    titleText?: TypographyPresentationProps;
    subTitle?: TypographyPresentationProps;
    infoText?: TypographyPresentationProps;
    formWrapper?: InjectableCss;
    form?: InjectableCss;
    backButton?: ButtonPresentationProps;
    pardotFormWrapper?: InjectableCss;
    recaptcha?: ReCaptchaStyles;
}

export const eventRegistrationStyles: EventRegistrationStyles = {
    titleText: pageTitleStyles.titleText,
    subTitle: {
        variant: "h3",
        weight: 700,
    },
    infoText: {
        size: 16,
        lineHeight: "18px",
        weight: 500,
    },
    formWrapper: {
        css: css`
            margin: 30px 0 40px 0;
            padding-top: 30px;
            padding-bottom: 30px;
        `,
    },
    form: {
        css: css`
            padding-bottom: 15px;
            margin-bottom: 30px;
            border-bottom: 1px solid ${getColor("common.ui2")};
            max-width: 100%;
        `,
    },
    backButton: {
        variant: "secondary",
    },
    pardotFormWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
        `,
    },
    recaptcha: {
        wrapper: {
            css: css`
                margin-top: 20px;
            `,
        },
    },
};

const styles = eventRegistrationStyles;

const dateOptions: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
};

const EventRegistration: React.FC<Props> = ({
    productContext: { product },
    eventData,
    hasEnoughSeats,
    language,
    countries,
    contactUsOptions,
    history,
    toaster,
    loadCurrentCountries,
    loadSystemList,
    customSettings,
}) => {
    const goToEventDetailsPage = useCallback(() => {
        history.push(product.canonicalUrl);
    }, [product.canonicalUrl]);

    const successCallback = useCallback(() => {
        history.push(product.canonicalUrl);
        const successMessage = !hasEnoughSeats
            ? "McMc_Event_AddToWaitlist_Success_Message"
            : "McMc_Event_Registration_Success_Message";
        toaster.addToast({
            body: siteMessage(successMessage),
            messageType: "success",
        });
        return false;
    }, [product.canonicalUrl, hasEnoughSeats]);

    const eventRegistrationFormState = useState<EventRegistrationFormState>(
        cloneDeep(initailEventRegistrationFormState),
    );

    useEffect(() => {
        if (!contactUsOptions) {
            loadSystemList({ name: "ContactUsOptions" });
        }
        if (!countries) {
            loadCurrentCountries();
        }
    }, []);

    const startDate = eventData?.startDate ? new Date(eventData.startDate) : undefined;
    const endDate = eventData?.endDate ? new Date(eventData.endDate) : undefined;

    const startDateDisplay = startDate
        ? getLocalizedDateTime({
              dateTime: startDate,
              language,
              options: dateOptions,
          })
        : "";
    const endDateDisplay = endDate
        ? getLocalizedDateTime({
              dateTime: endDate,
              language,
              options: dateOptions,
          })
        : "";

    let dateMessage;
    if (endDate && startDate) {
        dateMessage = translate("{0} - {1}", startDateDisplay, endDateDisplay);
    } else {
        dateMessage = endDate || startDate;
    }

    const { addToWaitListPardotUrl } = customSettings;
    const pardotFormUrl = hasEnoughSeats ? eventData?.formType : addToWaitListPardotUrl;

    return (
        <StyledWrapper>
            <Typography {...styles.titleText} data-test-selector="EventRegistrationPageTitle">
                {product.productTitle}
            </Typography>
            <Typography {...styles.infoText}>
                {eventData?.location}
                {eventData?.location && dateMessage ? " : " : ""}
                {dateMessage}
            </Typography>
            {eventData && (
                <StyledBox {...styles.formWrapper}>
                    {hasEnoughSeats && !eventData.formType && (
                        <EventRegistrationForm
                            initialState={eventRegistrationFormState}
                            countries={countries}
                            contactUsOptions={contactUsOptions}
                            goToEventDetailsPage={goToEventDetailsPage}
                            successCallback={successCallback}
                        />
                    )}
                    {(!hasEnoughSeats || eventData.formType) && (
                        <StyledWrapper {...styles.pardotFormWrapper}>
                            {!hasEnoughSeats && (
                                <Typography {...styles.subTitle}>{translate("Add to Waitlist")}</Typography>
                            )}
                            <StyledWrapper {...styles.form}>
                                <PardotForm formUrl={pardotFormUrl} />
                            </StyledWrapper>
                            <Button
                                {...styles.backButton}
                                onClick={goToEventDetailsPage}
                                data-test-selector="EventRegistrationBackButton"
                            >
                                {translate("Back To Event Details")}
                            </Button>
                        </StyledWrapper>
                    )}
                </StyledBox>
            )}
        </StyledWrapper>
    );
};

const EventRegistrationWidget: WidgetModule = {
    component: withToaster(
        withHistory(withProductContext(connect(mapStateToProps, mapDispatchToProps)(EventRegistration))),
    ),
    definition: {
        group: "Product Details",
        allowedContexts: ["EventRegistrationPage" as any],
    },
};

export default EventRegistrationWidget;
